// Property Management add-on: plan definitions and the agreement body.
//
// Prices here are only fallbacks - the live figures come from api_configs so
// they can be changed without a deploy. Keys map to the api_configs fields.
//
// The agreement is a binding contract governed by Bahamian law, so its wording
// is deliberately kept out of i18n and left in the language it was drafted in.
// Surrounding UI labels are translated as normal.

export const MANAGEMENT_PLANS = [
  {
    key: 'remote_owner',
    name: 'The Remote Owner Plan',
    audience: 'Off-island landlords and long-term rentals',
    summary: 'Rent collection, maintenance coordination and bill payments while you are away.',
    // Charged every month for this property
    monthlyCostField: 'management_remote_owner_monthly_cost',
    monthlyCostFallback: 20,
    monthlyCostUnit: 'unit/mo',
    services: [
      'Rent Collection & Monthly Reporting',
      'Bill Payment: BEC, Water, Internet, Cable, HOA/Strata',
      'Maintenance Coordination + Quarterly Inspections'
    ],
    // Billed when they happen rather than up front
    usageFees: [
      { label: 'Maintenance job coordinated', field: 'management_job_cost', fallback: 30, unit: 'job' },
      { label: 'Bill paid on your behalf', field: 'management_bill_cost', fallback: 35, unit: 'bill' }
    ],
    allowsBillAddon: false
  },
  {
    key: 'five_star_host',
    name: 'The 5-Star Host Plan',
    audience: 'Busy Airbnb and short term hosts',
    summary: 'Listing setup, guest messaging, calendar, arrivals and departures, cleaner coordination.',
    // No standing monthly charge - this plan earns per booking
    monthlyCostField: null,
    monthlyCostFallback: 0,
    monthlyCostUnit: 'mo',
    services: [
      'Airbnb Listing Setup, Messaging & Calendar Management',
      'Cleaner Coordination + Quality Checks',
      'Bill Payment: BEC, Internet (optional add-on)',
      'Monthly Performance Reporting'
    ],
    usageFees: [
      { label: 'Per confirmed booking', field: 'management_five_star_booking_cost', fallback: 30, unit: 'booking' }
    ],
    allowsBillAddon: true,
    billAddonField: 'management_bill_addon_monthly_cost',
    billAddonFallback: 35
  }
];

export const planByKey = key => MANAGEMENT_PLANS.find(p => p.key === key);

export const YARD_FREQUENCIES = ['weekly', 'bi-weekly', 'monthly', 'as needed'];
export const INSPECTION_FREQUENCIES = ['monthly', 'quarterly', 'only if issue reported'];
export const INSPECTION_DAYS = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
export const BILL_TYPES = ['BEC', 'Water', 'Internet', 'Cable', 'HOA/Strata'];
export const BILLING_CYCLES = ['monthly', 'quarterly'];
export const ACCESS_METHODS = ['drop off keys', 'lockbox', 'other'];

// Repairs under this figure proceed without asking the owner first
export const MAINTENANCE_APPROVAL_LIMIT = 200;

// Sent with the enrolment and stored against it, so a signature can always be
// tied back to the exact wording the owner was shown. Bump this whenever the
// text below changes.
export const AGREEMENT_VERSION = '2026-09-13';

export const AGREEMENT_SECTIONS = [
  {
    heading: '1. Property & Plan',
    body: [
      'This Agreement covers the property identified above and the plan selected in step one. '
      + 'The selected plan, its fees and any optional add-ons form part of this Agreement.'
    ]
  },
  {
    heading: '2. Services Included',
    body: [
      'The Remote Owner Plan: (1) Rent Collection & Monthly Reporting. (2) Bill Payment: BEC, Water, '
      + 'Internet, Cable, HOA/Strata. (3) Maintenance Coordination + Quarterly Inspections.',
      'The 5-Star Host Plan: (1) Airbnb Listing Setup, Messaging & Calendar Management. '
      + '(2) Cleaner Coordination + Quality Checks. (3) Bill Payment: BEC, Internet - optional add-on. '
      + '(4) Monthly Performance Reporting.'
    ]
  },
  {
    heading: '3. Fees & Payment',
    body: [
      'Client agrees to pay Rentalfaqz.com the fees shown for the selected plan. '
      + 'Remote Owner Plan: $20 per unit per month, plus $30 per maintenance job coordinated, '
      + 'plus $35 per bill paid. 5-Star Host Plan: $30 per booking, plus $35 per month for bill '
      + 'payment where the optional add-on is selected.',
      'Fees are due on the 1st of each month. Bill payment funds must be provided 5 business days '
      + 'before the due date.',
      'Late Payment: unpaid balances after 15 days are subject to 2% monthly interest. Client agrees '
      + 'to pay all collection costs and attorney fees.'
    ]
  },
  {
    heading: '4. Bill Payment Authority',
    body: [
      'Client authorizes Rentalfaqz.com to pay utilities and HOA from funds provided by Client. '
      + 'Rentalfaqz.com is not responsible for service interruptions if funds are not provided on time.',
      'Bill payment service under the 5-Star Host Plan applies only if the optional add-on is selected.'
    ]
  },
  {
    heading: '5. Maintenance Approval',
    body: [
      'Rentalfaqz.com may approve repairs up to $200 without prior consent. Any repair over $200 '
      + 'requires written approval from Client. Client is responsible for all repair costs.'
    ]
  },
  {
    heading: '6. Insurance',
    body: [
      'Client agrees to maintain adequate property and liability insurance for the Property during the '
      + 'term of this Agreement and agrees to name Rentalfaqz.com as an additional interested party '
      + 'upon request.'
    ]
  },
  {
    heading: '7. Term & Termination',
    body: [
      'Month-to-month. Either party may terminate with 30 days written notice. Client remains liable '
      + 'for all fees, bills, and obligations incurred prior to the termination date.'
    ]
  },
  {
    heading: '8. Liability & Indemnification',
    body: [
      'Rentalfaqz.com acts as a coordinator only. Rentalfaqz.com is not liable for tenant actions, '
      + 'contractor performance, property damage, or utility service issues.',
      'Client agrees to indemnify, defend, and hold harmless Rentalfaqz.com from any claims, damages, '
      + 'losses, or lawsuits arising from the Property, tenants, or contractors.'
    ]
  },
  {
    heading: '9. Electronic Agreement',
    body: [
      'By typing your full name below and checking the box, you agree that this electronic signature is '
      + 'legally binding and that you have read and agree to all terms above. This Agreement is governed '
      + 'by the laws of The Commonwealth of The Bahamas.'
    ]
  }
];
